import React from 'react';

import Alert from '@material-ui/lab/Alert';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import PropTypes from 'prop-types';
import TextField from '@material-ui/core/TextField';
import { connect, useDispatch } from 'react-redux';
import { GiDeliveryDrone } from 'react-icons/gi';

import Icons from 'consts/icons';
import { addAlert } from 'store/actions/alert/alerts';
import { patchRequest } from 'helpers/apiRequests';
import { setLanguage } from 'store/actions/language/language';
import { useParams, useHistory } from 'react-router-dom';
import { useLoginStyles } from 'consts/styles/Login';

import Copyright from './Copyright';

const PasswordReset = props => {
  const { language } = props;

  const dispatch = useDispatch();

  const [siteLang, setSiteLang] = React.useState('en');
  const [showSuccessMessage, setShowSuccessMessage] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState(false);

  const { uidb64, token } = useParams();

  const history = useHistory();

  const changeLanguage = () => {
    const langToShow = siteLang === 'pl' ? 'en' : 'pl';
    setSiteLang(langToShow);
    dispatch(setLanguage(langToShow));
  };

  const [values, setValues] = React.useState({
    password: '',
    rePassword: '',
  });

  const classes = useLoginStyles();

  const handleChange = event => {
    const { target } = event;
    const { name, value } = target;
    setError(false);
    setValues({
      ...values,
      [name]: value,
    });
  };

  const validate = () => {
    if (values.password === values.rePassword) return true;
    setError(true);
    return false;
  };

  const setPassword = () => {
    if (!validate()) return;
    patchRequest('setNewPassword', {
      password: values.password,
      token,
      uidb64,
    })
      .then(result => {
        if (result.status === 200) setShowSuccessMessage(true);
        else setErrorMsg(true);
        dispatch(
          addAlert({
            title: language.alerts.dataProccessError.title_,
            desc: result.response.data.error
              ? result.response.data.error
              : result.message,
            type: 'error',
          })
        );
      })
      .catch(error => {
        setErrorMsg(true);
        dispatch(
          addAlert({
            title: language.alerts.loginError.title_,
            desc: error.message,
            type: 'error',
          })
        );
      });
  };

  const handleEnterClick = event => {
    if (event.key === 'Enter') {
      setPassword();
    }
  };

  const handleErrors = () => {
    if (showSuccessMessage) {
      return (
        <Alert
          severity="success"
          className={`${classes.Login}__message ${classes.Login}__message--border--success`}
        >
          {language.resetPassword.emailPage.successMsg_}
        </Alert>
      );
    }
    if (errorMsg) {
      return (
        <Alert
          severity="error"
          className={`${classes.Login}__message ${classes.Login}__message--border--error`}
        >
          {language.alerts.loginError.desc_}
        </Alert>
      );
    }
    return (
      <div
        className={`${classes.Login}__message ${classes.Login}__message--hidden`}
      >
        &nbsp;
      </div>
    );
  };

  const handleLogin = () => {
    history.push('/login');
  };

  return (
    <div className={classes.Login}>
      {/* Animated background */}
      <div className={`${classes.Login}__bg-grid`} />
      <div className={`${classes.Login}__bg-radar`} />
      <div className={`${classes.Login}__bg-ring ${classes.Login}__bg-ring--1`} />
      <div className={`${classes.Login}__bg-ring ${classes.Login}__bg-ring--2`} />
      <div className={`${classes.Login}__bg-ring ${classes.Login}__bg-ring--3`} />
      <div className={`${classes.Login}__bg-ring ${classes.Login}__bg-ring--4`} />
      <div className={`${classes.Login}__bg-ring ${classes.Login}__bg-ring--5`} />
      <div className={`${classes.Login}__bg-ring ${classes.Login}__bg-ring--6`} />
      <div className={`${classes.Login}__bg-ring ${classes.Login}__bg-ring--7`} />
      <div className={`${classes.Login}__bg-scan`} />

      {/* Language switcher */}
      <Button
        className={`${classes.Login}__lang`}
        onClick={() => changeLanguage()}
      >
        <Icons.Misc.Language className={`${classes.Login}__lang__icon`} />
        <div className={`${classes.Login}__lang__text`}>
          {siteLang === 'pl' ? 'PL' : 'EN'}
        </div>
      </Button>

      {/* Branding */}
      <div className={`${classes.Login}__branding`}>
        <GiDeliveryDrone className={`${classes.Login}__branding__logo`} />
        <div className={`${classes.Login}__branding__title`}>ZODIAC</div>
        <div className={`${classes.Login}__branding__subtitle`}>
          Drone Traffic Management
        </div>
        <div className={`${classes.Login}__branding__divider`} />
      </div>

      {/* Card */}
      <div className={`${classes.Login}__card-glow`} />
      <div className={`${classes.Login}__card`}>
        <div className={`${classes.Login}__card__header`}>
          {language.resetPassword.setPassword.header_}
        </div>
        <form className={`${classes.Login}__card__form`} noValidate>
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            error={error}
            id="password"
            label={language.resetPassword.setPassword.password_}
            name="password"
            autoComplete="password"
            type="password"
            autoFocus
            onChange={handleChange}
            onKeyPress={handleEnterClick}
            value={values.password}
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            error={error}
            id="rePassword"
            label={language.resetPassword.setPassword.rePassword_}
            name="rePassword"
            type="password"
            onChange={handleChange}
            onKeyPress={handleEnterClick}
            value={values.rePassword}
          />
          <Button
            type="button"
            fullWidth
            className={`${classes.Login}__card__submit`}
            onClick={setPassword}
            style={{ marginTop: 16 }}
          >
            {language.resetPassword.setPassword.button_}
          </Button>
          <Grid container className={`${classes.Login}__card__links`}>
            <Grid item xs>
              <Link variant="body2" onClick={handleLogin}>
                <Icons.Navigation.BackToLogIn />
                &nbsp;&nbsp;{language.resetPassword.emailPage.backMsg_}
              </Link>
            </Grid>
          </Grid>
          {handleErrors()}
        </form>
      </div>

      {/* Copyright */}
      <Box mt={4}>
        <Copyright />
      </Box>
    </div>
  );
};

PasswordReset.propTypes = {
  language: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  language: state.language,
});

export default connect(mapStateToProps)(PasswordReset);
